/* talk·point — Story-Viewer v2 (specs/002-story-viewer-v2.md).
   Markup: snippets/tp-story-v2.liquid (lazy via ?section_id=tp-story-v2) · Stile: tp-story-v2.css

   Getrennt von v1 (tp-story-viewer.js). Aktiv nur, wenn html[data-tp-stories="v2"]
   (Theme-Einstellung oder ?stories=v2, gesetzt in snippets/tp-category-bubbles.liquid).

   Gesten (P1): Wischen = Würfel zur nächsten/vorigen Story, folgt dem Finger ·
   Tippen rechts/links = weiter/zurück · Halten = Pause ohne Bedienelemente ·
   nach unten ziehen = schließen · nach oben wischen = Produkte aufklappen.
   Produkte (P2): Leiste, Liste mit Zustand-Filter und Preis-Sortierung, Energielabel
   mit Datenblatt im Blatt. Kein Warenkorb in der Story (Entscheidung 13.09.2026) —
   Produkte führen zur Produktseite.
   Segmente (P5–P7): Video (Ton per Knopf, Balken = Cliplänge), Bild mit Preis-Punkten,
   Folien (Problem → Lösung → Details), Blog-Beiträge als Karte, Rabattcode mit Countdown, Quiz (überspringbar, hält
   nach der Antwort, optional Erklärtext und Link zu einer Collection).

   Warum <dialog>: Top-Layer statt z-index, Esc/Fokus/inerter Hintergrund vom Browser. */

const ACTIVE = document.documentElement.dataset.tpStories === 'v2';
const SEEN_KEY = 'tp:seen-stories:v2';
// Segmentdauer in ms (§5.4). Video läuft so lang wie der Clip.
const SEG_DURATION = { image: 7000, slide: 8000, article: 10000, code: 12000, quiz: 14000 };
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const readSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch (e) { return new Set(); } };
// K10: alter Index-Schlüssel von v1 bleibt liegen (Rollback auf v1 behält seinen Stand).
const writeSeen = (set) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-100))); } catch (e) {} };
const markSeenBubbles = () => {
  const seen = readSeen();
  document.querySelectorAll('a[data-story="1"][data-story-handle]').forEach((b) => {
    b.classList.toggle('tp-bub--seen', seen.has(b.dataset.storyHandle));
  });
};
// Tracking defensiv: der Viewer darf nie am Tracking scheitern. Neue Typen (§7.2)
// verwirft tp-track.js, bis sie in beiden Whitelists stehen (P3) — kein Schaden.
const track = (type, props) => { try { window.TP && window.TP.track(type, props); } catch (e) {} };

class TpStoryV2 extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    if (this.parentElement !== document.body) { document.body.appendChild(this); return; }
    this._ready = true;
    this.dialog = this.querySelector('dialog');
    this.stage = this.querySelector('[data-stage]');
    this.bg = this.querySelector('[data-bg]');
    this.faces = [...this.querySelectorAll('.tps-face')];
    this.sheet = this.querySelector('[data-sheet]');
    this.cur = -1;
    this.seg = 0;
    this.muted = true;
    this.dragP = 0;
    this.elapsed = 0;
    this.paused = false;
    this.holding = false;
    this.dragging = false;
    this.animating = false;
    this.sheetFace = -1;

    this.dialog.addEventListener('click', (e) => this._onClick(e));
    this.dialog.addEventListener('keydown', (e) => this._onKey(e));
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.sheetOpen ? this.closeSheet() : this.close('key'); });
    // Sicherheitsnetz: egal wie der Dialog zugeht — kein Video darf weiterlaufen.
    this.dialog.addEventListener('close', () => { this.querySelectorAll('.tps-video').forEach((v) => { v.pause(); v.muted = true; }); });
    this._initGestures();
    this._initSheetGrip();
    addEventListener('resize', () => { if (this.dialog.open && this.cur >= 0) this._placeHotspots(); });
  }

  get sheetOpen() { return this.dialog.classList.contains('is-sheet'); }
  face(i = this.cur) { return this.faces[i]; }
  story(i = this.cur) { return this.faces[i]?.dataset.story; }
  collection(i = this.cur) { return this.faces[i]?.dataset.collection || undefined; }

  /* ── Öffnen / Schließen ───────────────────────────────────────────────── */
  open(index, bubble) {
    if (!this.faces.length) return;
    const i = Math.max(0, Math.min(this.faces.length - 1, index));
    this.bubble = bubble || null;
    this.openedAt = performance.now();
    this.paused = false;
    this.seg = 0;
    this.dialog.classList.remove('is-paused', 'is-holding', 'is-sheet');
    this.faces.forEach((f) => { f._nudged = false; });
    if (!this.dialog.open) this.dialog.showModal();
    track('bubble_opened', { bubble_index: i, collection: this.collection(i), story: this.story(i) });
    track('story_viewed', { bubble_index: i, collection: this.collection(i), story: this.story(i) });
    this.setCur(i);
    this._fly(true);
    this._coach();
    this._startLoop();
  }

  close(how) {
    if (!this.dialog.open || this.animating) return;
    this._leaveSeg(how);
    track('story_closed', { story: this.story(), card_index: this.cur, segment: this.kind, dwell_ms: Math.round(performance.now() - this.openedAt), how });
    const closingFace = this.cur;
    this.animating = true;
    this._leaveFace(closingFace);
    this.closeSheet();
    const done = () => {
      this._leaveFace(closingFace);
      this.animating = false;
      this.dragging = false;
      this.stage.style.transform = '';
      this.stage.style.borderRadius = '';
      this.bg.style.opacity = '';
      this.cur = -1;
      this._layout(0);
      cancelAnimationFrame(this._raf);
      this._raf = 0;
      this.dialog.close();
    };
    this._fly(false, done);
  }

  // Öffnen: Bühne kippt aus der Tiefe herein. Schließen: Flip-away (rotateY + scale + Blende),
  // die Bubble quittiert mit einem Ping-Ring (::after in tp-category-bubbles.css). Kein Flug zur
  // Bubble mehr (ergab bei der hochkanten Bühne ein Oval); ohne sichtbare Bubble entfällt nur der Ping.
  _fly(opening, done) {
    if (reduceMotion()) { done?.(); return; }
    const rest = { transform: 'perspective(1200px) rotateY(0) scale(1)', opacity: 1 };
    const frames = opening
      ? [{ transform: 'perspective(1200px) rotateY(-18deg) scale(.92)', opacity: 0 }, rest]
      : [{ transform: this.stage.style.transform || rest.transform, opacity: 1 }, { transform: 'perspective(1200px) rotateY(25deg) scale(.9)', opacity: 0 }];
    const a = this.stage.animate(frames, { duration: opening ? 280 : 240, easing: opening ? 'cubic-bezier(.2,.8,.2,1)' : 'cubic-bezier(.4,0,1,1)', fill: opening ? 'none' : 'forwards' });
    this.bg.animate(opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: getComputedStyle(this.bg).opacity }, { opacity: 0 }], { duration: opening ? 300 : 280, fill: opening ? 'none' : 'forwards' });
    if (!opening) {
      // Ziel: Bubble-Ring (mobil) oder die Hero-Story-Kachel (Desktop, overflow:hidden → Schatten-Puls statt ::after)
      const bub = this._bubbleFor(this.cur);
      const ring = bub?.querySelector('.tp-bub__ring');
      const r = (ring || bub)?.getBoundingClientRect();
      if (r && r.width && r.bottom > 0 && r.top < innerHeight) {
        const opts = { duration: 520, delay: 140, easing: 'ease-out' };
        try {
          if (ring) ring.animate([{ transform: 'scale(.9)', opacity: 1 }, { transform: 'scale(1.45)', opacity: 0 }], { ...opts, pseudoElement: '::after' });
          else { const g = getComputedStyle(bub).getPropertyValue('--tp-green').trim() || '#18cf72'; bub.animate([{ boxShadow: `0 0 0 0 ${g}` }, { boxShadow: '0 0 0 14px transparent' }], opts); }
        } catch (e) { /* alte Engines ohne pseudoElement */ }
      }
    }
    if (done) a.onfinish = () => { this.stage.getAnimations().forEach((x) => x.cancel()); this.bg.getAnimations().forEach((x) => x.cancel()); done(); };
  }

  _bubbleFor(i) {
    const handle = this.story(i);
    return (handle && document.querySelector(`a[data-story="1"][data-story-handle="${CSS.escape(handle)}"]`))
      || document.querySelector(`a[data-story="1"][data-story-index="${i}"]`);
  }

  _coach() {
    try { if (localStorage.getItem('tp:stories:coach')) return; localStorage.setItem('tp:stories:coach', '1'); } catch (e) { return; }
    const c = this.querySelector('[data-coach]');
    setTimeout(() => c.classList.add('is-on'), 450);
    setTimeout(() => c.classList.remove('is-on'), 3200);
  }

  /* ── Stories ──────────────────────────────────────────────────────────── */
  setCur(i) {
    const prev = this.cur;
    if (prev >= 0 && prev !== i) this._leaveFace(prev);
    this.cur = i;
    for (let k = i - 1; k <= i + 1; k++) this._loadFace(k);
    const seen = readSeen();
    seen.add(this.story(i));
    writeSeen(seen);
    markSeenBubbles();
    this._layout(0);
    this.showSeg(0);
    this._nudgeShelf(i);
  }

  /* ── Segmente (§5.4): video → image → code → quiz ─────────────────────── */
  segs(i = this.cur) { return (this.faces[i]?.dataset.segs || 'image').split(','); }
  get kind() { return this.segs()[this.seg] || 'image'; }

  showSeg(n) {
    const f = this.face(), segs = this.segs();
    this.seg = Math.max(0, Math.min(segs.length - 1, n));
    this.elapsed = 0;
    this.hold = false;
    this.quizDone = false;
    const kind = segs[this.seg];
    f.dataset.seg = kind;
    f.querySelectorAll('.tps-bar i').forEach((b, k) => { b.style.width = k < this.seg ? '100%' : '0%'; });
    f.querySelectorAll('[data-layer]').forEach((l) => { l.hidden = l.dataset.layer !== kind; });
    this._closePops(f);
    const ask = f.querySelector('[data-quiz-ask]');
    if (ask) { ask.hidden = false; f.querySelectorAll('[data-quiz-result]').forEach((r) => { r.hidden = true; }); }
    const video = f.querySelector('.tps-video');
    if (video) {
      video.hidden = kind !== 'video';
      if (kind === 'video') {
        video.muted = this.muted;
        video.preload = 'auto';
        if (video.currentTime) video.currentTime = 0;
        video._q = 0;
        video._failed = false;
      } else video.pause();
    }
    if (kind === 'image') this._placeHotspots(f);
    track('story_card_viewed', { card_index: this.cur, collection: this.collection(), story: this.story(), segment: kind });
  }

  _leaveFace(i) {
    const v = this.faces[i]?.querySelector('.tps-video');
    if (v) { v.pause(); v.hidden = true; v.muted = true; }
  }

  // Quiz ohne Antwort verlassen → übersprungen, mit Art des Verlassens (§7.2).
  _leaveSeg(how) {
    if (this.cur < 0 || this.kind !== 'quiz' || this.quizDone) return;
    const layer = this.face().querySelector('[data-layer="quiz"]');
    track('story_quiz_skipped', { story: this.story(), quiz_id: layer?.dataset.quizId, how });
    this.quizDone = true;
  }

  _closePops(f = this.face()) {
    f.querySelectorAll('.tps-pop').forEach((p) => { p.hidden = true; });
    f.querySelectorAll('.tps-hot').forEach((h) => { h.hidden = false; });
  }

  // Punkte liegen in Prozent des unbeschnittenen 9:16-Bildes (§5.3); object-fit: cover herausrechnen.
  _placeHotspots(f = this.face()) {
    const hots = f.querySelectorAll('.tps-hot');
    if (!hots.length) return;
    const img = f.querySelector('.tps-img');
    const W = f.clientWidth, H = f.clientHeight;
    const nw = img.naturalWidth || 1080, nh = img.naturalHeight || 1920;
    // object-fit: contain, object-position: 50% 0 (tp-story-v2.css) — horizontal zentriert, oben bündig.
    const k = Math.min(W / nw, H / nh), dw = nw * k, dh = nh * k;
    hots.forEach((h) => {
      const left = (W - dw) / 2 + Number(h.dataset.x) * dw;
      h.style.left = `${left}px`;
      h.style.top = `${Number(h.dataset.y) * dh}px`;
      // Etikett zeigt nach links, wenn es rechts aus dem Bild liefe (Punkt in der rechten Hälfte).
      h.classList.toggle('is-flip', left + h.offsetWidth > W - 8);
    });
  }

  _loadFace(i) {
    const f = this.faces[i];
    if (!f) return;
    // Folien, Beitrag, Quiz-Ergebnis und Punkt-Karte liegen versteckt mit loading="lazy" und kämen
    // erst beim Einblenden. Für die offene Story und ihre Nachbarn gleich laden (nur bei Nutzung).
    f.querySelectorAll('.tps-layer img[loading="lazy"], .tps-pop img[loading="lazy"]').forEach((el) => { el.loading = 'eager'; });
    const img = f.querySelector('.tps-img');
    if (!img || img.getAttribute('src') || !img.dataset.src) return;
    const done = () => {
      f.classList.add('is-loaded');
      if (img.currentSrc) f.querySelector('.tps-media').style.setProperty('--tps-bg', `url("${img.currentSrc}")`);
      if (this.faces[this.cur] === f) this._placeHotspots(f);
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
  }

  // Leiste ruckt beim ersten Zeigen kurz an: „da kommt noch mehr".
  _nudgeShelf(i) {
    const f = this.faces[i], row = f?.querySelector('.tps-row');
    if (!row || f._nudged || reduceMotion() || row.scrollWidth <= row.clientWidth + 8) return;
    f._nudged = true;
    setTimeout(() => {
      if (this.cur !== i) return;
      row.scrollTo({ left: 64, behavior: 'smooth' });
      setTimeout(() => row.scrollTo({ left: 0, behavior: 'smooth' }), 650);
    }, 900);
  }

  next(how) {
    this._leaveSeg(how);
    if (this.seg < this.segs().length - 1) { this.showSeg(this.seg + 1); return; }
    if (this.cur < this.faces.length - 1) this._animateTo(-1, () => this.setCur(this.cur + 1));
    else this.close(how === 'timer' ? 'end' : how);
  }

  prev(how) {
    this._leaveSeg(how);
    if (this.seg > 0) { this.showSeg(this.seg - 1); return; }
    if (this.cur > 0) this._animateTo(1, () => this.setCur(this.cur - 1));
    else this.showSeg(0);
  }

  // Würfel: p in [-1, 1] ist der Anteil der Wischbewegung (negativ = zur nächsten).
  _layout(p) {
    this.dragP = p;
    const flat = reduceMotion();
    this.faces.forEach((f, i) => {
      const o = i - this.cur + p;
      const on = this.cur >= 0 && Math.abs(i - this.cur) <= 1 && Math.abs(o) < 1;
      f.classList.toggle('is-on', on);
      if (!on) { f.style.transform = ''; return; }
      if (flat) f.style.transform = `translateX(${o * 100}%)`;
      else {
        f.style.transformOrigin = o < 0 ? '100% 50%' : '0% 50%';
        f.style.transform = `translateX(${o * 100}%) rotateY(${o * 90}deg)`;
      }
      f.querySelector('.tps-shade').style.opacity = String(Math.abs(o) * 0.55);
    });
  }

  _animateTo(target, done) {
    this.animating = this.dragging = true;
    const from = this.dragP, t0 = performance.now(), dur = reduceMotion() ? 1 : 300;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      this._layout(from + (target - from) * e);
      if (k < 1) { requestAnimationFrame(step); return; }
      this.animating = this.dragging = false;
      done ? done() : this._layout(0);
    };
    requestAnimationFrame(step);
  }

  /* ── Takt ─────────────────────────────────────────────────────────────── */
  _startLoop() {
    if (this._raf) return;
    let last = 0;
    const loop = (t) => {
      this._raf = requestAnimationFrame(loop);
      const dt = last ? Math.min(t - last, 100) : 0;
      last = t;
      const f = this.face();
      if (!f || !f.classList.contains('is-loaded')) return;
      const kind = this.kind;
      const bar = f.querySelectorAll('.tps-bar i')[this.seg];
      // animating: Würfel-Wechsel und Schließ-Animation — sonst startete die Schleife ein
      // eben pausiertes Video während der 300 ms Schließen neu, und es lief hinter der Seite weiter.
      const blocked = this.paused || this.holding || this.dragging || this.sheetOpen || this.hold || this.animating;

      if (kind === 'video') {
        const v = f.querySelector('.tps-video');
        if (!v) return;
        if (blocked || v._failed) { if (!v.paused) v.pause(); }
        else if (v.paused && !v.ended && !v._req) {
          v._req = true;
          // Autoplay verweigert (z. B. Stromsparmodus): pausieren, Play-Knopf startet neu.
          v.play().catch(() => { v._failed = true; if (!this.paused) this.togglePause(); }).finally(() => { v._req = false; });
        }
        f.classList.toggle('is-buffering', !blocked && !v._failed && v.readyState < 3);
        if (v.duration) {
          if (bar) bar.style.width = `${(v.currentTime / v.duration) * 100}%`;
          const q = v.ended ? 100 : Math.floor((v.currentTime / v.duration) * 4) * 25;
          if (q > (v._q || 0)) { v._q = q; track('story_video_progress', { story: this.story(), collection: this.collection(), quartile: q }); }
        }
        if (v.ended) this.next('timer');
        return;
      }
      f.classList.remove('is-buffering');
      if (kind === 'code') this._tickCountdown(f);
      if (!blocked) this.elapsed += dt;
      const dur = SEG_DURATION[kind.split(':')[0]] || SEG_DURATION.image;   // slide:0 → slide
      if (bar) bar.style.width = `${Math.min(100, (this.elapsed / dur) * 100)}%`;
      if (this.elapsed >= dur) { this.elapsed = 0; this.next('timer'); }
    };
    this._raf = requestAnimationFrame(loop);
  }

  _tickCountdown(f) {
    const el = f.querySelector('[data-ends]');
    if (!el) return;
    const left = Math.max(0, Number(el.dataset.ends) * 1000 - Date.now());
    const h = Math.floor(left / 3600e3), m = Math.floor(left / 60e3) % 60, sec = Math.floor(left / 1e3) % 60;
    const pad = (n) => String(n).padStart(2, '0');
    const text = left ? `Gilt noch ${h >= 24 ? `${Math.floor(h / 24)} T ` : ''}${pad(h % 24)}:${pad(m)}:${pad(sec)}` : 'Abgelaufen';
    if (el.textContent !== text) el.textContent = text;
  }

  togglePause() {
    this.paused = !this.paused;
    this.dialog.classList.toggle('is-paused', this.paused);
    const v = this.kind === 'video' && this.face()?.querySelector('.tps-video');
    if (v && !this.paused) v._failed = false;
  }

  /* ── Gesten ───────────────────────────────────────────────────────────── */
  _initGestures() {
    const st = this.stage;
    let down = false, sx = 0, sy = 0, t0 = 0, axis = null, holdT = 0, W = 1, H = 1, hdY = null;

    st.addEventListener('pointerdown', (e) => {
      if (e.button > 0 || this.animating || this.sheetOpen) return;
      // Leistenkopf: nach oben ziehen klappt auf (Tippen erledigt der click).
      if (e.target.closest('.tps-shelf__hd')) { hdY = e.clientY; return; }
      // Folien (.tps-slide) sind reine Anzeige → Tippen/Wischen darauf steuert die Story.
      if (e.target.closest('button, a, .tps-shelf, .tps-sheet, .tps-scrim, .tps-pop, .tps-stk:not(.tps-slide__card)')) return;
      down = true; axis = null; sx = e.clientX; sy = e.clientY; t0 = performance.now();
      W = st.clientWidth; H = st.clientHeight;
      st.setPointerCapture(e.pointerId);
      holdT = setTimeout(() => { this.holding = true; this.dialog.classList.add('is-holding'); }, 220);
    });

    st.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      if (!axis) {
        if (Math.hypot(dx, dy) < 10) return;
        clearTimeout(holdT);
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : (dy > 0 ? 'down' : 'up');
        this.dragging = true;
        if (this.holding) { this.holding = false; this.dialog.classList.remove('is-holding'); }
      }
      if (axis === 'x') {
        let q = dx / W;
        if ((this.cur === 0 && q > 0) || (this.cur === this.faces.length - 1 && q < 0)) q *= 0.3;
        this._layout(Math.max(-1, Math.min(1, q)));
      } else if (axis === 'down') {
        const d = Math.max(0, dy);
        st.style.transform = `translateY(${d}px) scale(${1 - Math.min(0.4, (d / H) * 0.5)})`;
        st.style.borderRadius = Math.min(32, d / 5) + 'px';
        this.bg.style.opacity = String(Math.max(0, 1 - (d / H) * 1.6));
      } else {
        const shelf = this.face().querySelector('.tps-shelf');
        if (shelf) shelf.style.transform = `translateY(${Math.max(-70, Math.min(0, dy)) * 0.7}px)`;
      }
    });

    const end = (e, cancelled) => {
      if (hdY !== null) {
        const d = e.clientY - hdY;
        hdY = null;
        if (!cancelled && d < -20) this.openSheet(this.cur, 'swipe');
        return;
      }
      if (!down) return;
      down = false;
      clearTimeout(holdT);
      const dx = e.clientX - sx, dy = e.clientY - sy, dt = Math.max(1, performance.now() - t0);
      if (!axis) {
        if (this.holding) { this.holding = false; this.dialog.classList.remove('is-holding'); return; }
        if (!cancelled && dt < 300) {
          // Offene Produktkarte eines Punkts: Tippen daneben schließt nur die Karte.
          if (this.face().querySelector('.tps-pop:not([hidden])')) { this._closePops(); this.hold = false; return; }
          // Der click zu diesem Tipp kommt erst danach — sonst träfe er den Sticker, der
          // gerade unter dem Finger erscheint (z. B. eine Quiz-Antwort).
          this._swallowClickUntil = performance.now() + 450;
          const x = e.clientX - st.getBoundingClientRect().left;
          x < W * 0.3 ? this.prev('tap') : this.next('tap');
        }
        return;
      }
      if (axis === 'x') {
        const q = this.dragP, v = dx / dt, last = this.faces.length - 1;
        if ((q < -0.2 || v < -0.45) && this.cur < last) { this._leaveSeg('swipe'); this._animateTo(-1, () => this.setCur(this.cur + 1)); }
        else if ((q > 0.2 || v > 0.45) && this.cur > 0) { this._leaveSeg('swipe'); this._animateTo(1, () => this.setCur(this.cur - 1)); }
        else if ((q < -0.1 || v < -0.45) && this.cur === last) { this._layout(0); this.dragging = false; this.close('swipe'); }
        else this._animateTo(0);
      } else if (axis === 'down') {
        this.dragging = false;
        if (dy > H * 0.16 || dy / dt > 0.5) this.close('swipe_down');
        else {
          const t = st.style.transform;
          st.style.transform = ''; st.style.borderRadius = ''; this.bg.style.opacity = '';
          st.animate([{ transform: t }, { transform: 'none' }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' });
        }
      } else {
        this.dragging = false;
        const shelf = this.face().querySelector('.tps-shelf');
        if (shelf) shelf.style.transform = '';
        if (dy < -50) this.openSheet(this.cur, 'swipe');
      }
    };
    st.addEventListener('pointerup', (e) => end(e, false));
    st.addEventListener('pointercancel', (e) => end(e, true));
    st.addEventListener('contextmenu', (e) => { if (!e.target.closest('a')) e.preventDefault(); });
  }

  /* ── Klicks ───────────────────────────────────────────────────────────── */
  _onClick(e) {
    if (performance.now() < (this._swallowClickUntil || 0)) { e.preventDefault(); this._swallowClickUntil = 0; return; }
    const el = e.target.closest('[data-act]');
    // Klick auf den dunklen Rand (Desktop) schließt.
    if (!el) { if (e.target === this.dialog || e.target === this.bg) this.close('backdrop'); return; }
    const act = el.dataset.act;
    const card = el.closest('.tps-card');
    switch (act) {
      case 'close': this.close('button'); break;
      case 'pause': this.togglePause(); break;
      case 'prev': this.prev('button'); break;
      case 'next': this.next('button'); break;
      case 'expand':
        // Nach oben gezogen hat das Blatt schon geöffnet; der folgende click zählt nicht doppelt.
        if (!this.sheetOpen) this.openSheet(this.cur, 'tap');
        break;
      case 'sheet-close': this.closeSheet(); break;
      case 'label': e.preventDefault(); this.openLabel(el); break;
      case 'filter': this._filter = el.dataset.cond; this._renderGrid(); break;
      case 'sort': this._sortPrice = !this._sortPrice; this._renderGrid(); break;
      case 'back': this.openSheet(this.sheetFace); break;
      case 'product':
        track('story_product_clicked', { story: this.story(), collection: this.collection(), product_id: card?.dataset.productId, position: Number(card?.dataset.pos), source: this._source(el) });
        break; // Navigation läuft normal, sendBeacon überlebt den Seitenwechsel.
      case 'sound': {
        this.muted = !this.muted;
        const v = this.face().querySelector('.tps-video');
        if (v) v.muted = this.muted;
        this.dialog.classList.toggle('is-sound', !this.muted);
        el.setAttribute('aria-pressed', String(!this.muted));
        el.setAttribute('aria-label', this.muted ? 'Ton an' : 'Ton aus');
        break;
      }
      case 'hot': {
        const pop = this.face().querySelector(`[data-pop-card="${el.dataset.pop}"]`);
        if (!pop) break;
        this.face().querySelectorAll('.tps-hot').forEach((h) => { h.hidden = true; });
        pop.hidden = false;
        this.hold = true;
        track('story_product_clicked', { story: this.story(), collection: this.collection(), product_id: pop.querySelector('.tps-card')?.dataset.productId, position: Number(el.dataset.pop), source: 'hotspot' });
        break;
      }
      case 'pop-close': this._closePops(); this.hold = false; break;
      case 'copy':
        try { navigator.clipboard.writeText(el.dataset.code); } catch (err) {}
        track('story_code_used', { story: this.story(), code: el.dataset.code, action: 'copy' });
        this.toast(`${el.dataset.code} kopiert`);
        break;
      case 'redeem': track('story_code_used', { story: this.story(), code: el.dataset.code, action: 'redeem' }); break;
      case 'skip': this.next('button'); break;
      case 'quiz': {
        const layer = el.closest('[data-layer="quiz"]');
        const result = [...layer.querySelectorAll('[data-quiz-result]')].find((r) => r.dataset.quizResult === el.dataset.answer);
        if (!result) break;
        layer.querySelector('[data-quiz-ask]').hidden = true;
        result.hidden = false;
        this.quizDone = true;
        this.hold = true;
        track('story_quiz_answered', { story: this.story(), quiz_id: layer.dataset.quizId, answer_id: el.dataset.answer, product_id: result.dataset.productId });
        break;
      }
      case 'resume': this.hold = false; this.next('button'); break;
      case 'article':
        track('story_article_clicked', { story: this.story(), article: el.dataset.article, source: el.dataset.source || 'card' });
        break; // Navigation zum Beitrag läuft normal.
      case 'quiz-collection':
        track('collection_clicked', { collection: el.dataset.collection, story: this.story(), source: 'quiz' });
        break; // Navigation zur Collection läuft normal.
      case 'collection':
        track('collection_clicked', { collection: this.collection(this.sheetFace), story: this.story(this.sheetFace) });
        break;
      default: break;
    }
  }

  _source(el) {
    if (el.closest('.tps-pop')) return 'hotspot';
    if (el.closest('.tps-slide')) return 'slide';
    if (el.closest('[data-quiz-result]')) return 'quiz';
    return this.sheetOpen ? 'sheet' : 'shelf';
  }

  _onKey(e) {
    if (e.target.closest('input, textarea')) return;
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-act="expand"]')) { e.preventDefault(); this.openSheet(this.cur, 'key'); return; }
    if (this.sheetOpen) return;
    if (e.key === 'ArrowRight') this.next('key');
    else if (e.key === 'ArrowLeft') this.prev('key');
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.openSheet(this.cur, 'key'); }
    else if (e.key === ' ' && !e.target.closest('button, a')) { e.preventDefault(); this.togglePause(); }
  }

  /* ── Blatt ────────────────────────────────────────────────────────────── */
  openSheet(i, how) {
    const shelf = this.faces[i]?.querySelector('.tps-shelf');
    if (!shelf) return;
    this.sheetFace = i;
    this._filter = 'all';
    this._sortPrice = false;
    this.querySelector('[data-sheet-title]').textContent = shelf.dataset.title;
    this.querySelector('[data-sheet-sub]').textContent = shelf.dataset.sub;
    this.querySelector('[data-sheet-ft]').hidden = false;
    this.querySelector('[data-act="collection"]').href = shelf.dataset.url;
    this._renderGrid();
    this.dialog.classList.add('is-sheet');
    if (how) track('story_shelf_expanded', { story: this.story(i), collection: this.collection(i), how });
  }

  _renderGrid() {
    const shelf = this.faces[this.sheetFace].querySelector('.tps-shelf');
    const cards = [...shelf.querySelectorAll('.tps-row .tps-card')];
    const conds = [...new Set(cards.map((c) => c.dataset.cond).filter(Boolean))];
    const tools = this.querySelector('[data-sheet-tools]');
    tools.hidden = false;
    tools.innerHTML = '';
    const chip = (label, attrs, pressed) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tps-chip';
      b.textContent = label;
      b.setAttribute('aria-pressed', String(pressed));
      Object.entries(attrs).forEach(([k, v]) => b.setAttribute(k, v));
      tools.appendChild(b);
      return b;
    };
    if (conds.length > 1) {
      chip('Alle', { 'data-act': 'filter', 'data-cond': 'all' }, this._filter === 'all');
      conds.forEach((c) => chip(c, { 'data-act': 'filter', 'data-cond': c }, this._filter === c));
    }
    chip('Preis ↑', { 'data-act': 'sort' }, this._sortPrice).classList.add('tps-chip--sort');

    let list = cards.filter((c) => this._filter === 'all' || c.dataset.cond === this._filter);
    if (this._sortPrice) list = list.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
    const grid = document.createElement('div');
    grid.className = 'tps-grid';
    list.forEach((c) => {
      const clone = c.cloneNode(true);
      clone.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      grid.appendChild(clone);
    });
    const body = this.querySelector('[data-sheet-body]');
    body.replaceChildren(grid);
    body.scrollTop = 0;
  }

  openLabel(el) {
    const card = el.closest('.tps-card');
    const fromSheet = this.sheetOpen && this.sheetFace >= 0;
    track('story_label_opened', { story: this.story(), product_id: el.dataset.productId || card?.dataset.productId, eec: el.dataset.eecClass });
    if (!fromSheet) this.sheetFace = this.cur;
    this.querySelector('[data-sheet-title]').textContent = 'EU-Energielabel';
    this.querySelector('[data-sheet-sub]').textContent = `Energieeffizienzklasse ${el.dataset.eecClass}`;
    this.querySelector('[data-sheet-tools]').hidden = true;
    this.querySelector('[data-sheet-ft]').hidden = true;
    const body = this.querySelector('[data-sheet-body]');
    body.innerHTML = '';
    if (fromSheet) {
      const back = document.createElement('button');
      back.type = 'button'; back.className = 'tps-back'; back.dataset.act = 'back'; back.textContent = '‹ Zurück zu den Produkten';
      body.appendChild(back);
    }
    const plate = document.createElement('div');
    plate.className = 'tps-label';
    const img = document.createElement('img');
    img.src = el.dataset.labelSrc;
    img.alt = `EU-Energielabel, Klasse ${el.dataset.eecClass}`;
    plate.appendChild(img);
    const meta = document.createElement('p');
    meta.className = 'tps-label-meta';
    const full = document.createElement('a');
    full.href = el.href; full.target = '_blank'; full.rel = 'noopener'; full.textContent = 'Label in voller Größe';
    meta.append(full);
    if (el.dataset.fiche) {
      const fiche = document.createElement('a');
      fiche.href = el.dataset.fiche; fiche.target = '_blank'; fiche.rel = 'noopener'; fiche.textContent = 'EU-Produktdatenblatt';
      meta.append(' · ', fiche);
    }
    body.append(plate, meta);
    body.scrollTop = 0;
    this.dialog.classList.add('is-sheet');
  }

  closeSheet() {
    this.dialog.classList.remove('is-sheet');
  }

  _initSheetGrip() {
    const grip = this.querySelector('[data-grip]');
    let y = null;
    grip.addEventListener('pointerdown', (e) => { y = e.clientY; grip.setPointerCapture(e.pointerId); this.sheet.style.transition = 'none'; });
    grip.addEventListener('pointermove', (e) => { if (y !== null) this.sheet.style.transform = `translateY(${Math.max(0, e.clientY - y)}px)`; });
    const end = (e) => {
      if (y === null) return;
      const d = e.clientY - y;
      y = null;
      this.sheet.style.transition = '';
      this.sheet.style.transform = '';
      if (d > 80) this.closeSheet();
    };
    grip.addEventListener('pointerup', end);
    grip.addEventListener('pointercancel', end);
  }

  toast(msg) {
    const t = this.querySelector('[data-toast]');
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(this._toastT);
    this._toastT = setTimeout(() => t.classList.remove('is-on'), 1800);
  }
}

if (ACTIVE && !customElements.get('tp-story-v2')) {
  customElements.define('tp-story-v2', TpStoryV2);

  /* ── Lazy-Fetch + Mount (Muster wie v1, Perf-Loop I8) ───────────────────── */
  let mountPromise = null;
  const mounted = () => document.querySelector('tp-story-v2');

  const ensureViewer = () => {
    if (mounted()) return Promise.resolve(mounted());
    if (mountPromise) return mountPromise;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    mountPromise = fetch(`${root}?section_id=tp-story-v2`)
      .then((res) => { if (!res.ok) throw new Error(`tp-story-v2 ${res.status}`); return res.text(); })
      .then((text) => {
        const el = new DOMParser().parseFromString(text, 'text/html').querySelector('tp-story-v2');
        if (!el) throw new Error('tp-story-v2: Section ohne Viewer-Markup');
        if (!mounted()) document.body.appendChild(el);
        return mounted();
      })
      .catch((err) => { mountPromise = null; throw err; });
    return mountPromise;
  };

  const bubbleOf = (e) => e.target.closest && e.target.closest('a[data-story="1"]');

  document.addEventListener('pointerdown', (e) => { if (bubbleOf(e)) ensureViewer().catch(() => {}); }, true);
  // Desktop: Maus über der Bubble ist Absicht genug, der Viewer-Fetch startet ~200–400 ms vor dem Klick.
  document.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse' && bubbleOf(e)) ensureViewer().catch(() => {}); }, true);

  document.addEventListener('click', (e) => {
    const bubble = bubbleOf(e);
    if (!bubble || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = bubble.dataset.storyHandle;
    // Erster Fetch dauert spürbar → Ring dreht als Ladeanzeige (IT#16866, CSS in tp-category-bubbles.css).
    bubble.classList.add('tp-bub--loading');
    bubble.setAttribute('aria-busy', 'true');
    ensureViewer()
      // Über den Metaobjekt-Handle: v2-only-Stories haben keinen v1-Index (min_viewer).
      .then((viewer) => viewer.open(Math.max(0, viewer.faces.findIndex((f) => f.dataset.story === handle)), bubble))
      .catch(() => {
        // Viewer nicht ladbar → wenigstens zur Collection statt toter Tap (K11).
        const href = bubble.getAttribute('href');
        if (href && href !== '#') location.href = href;
      })
      .finally(() => { bubble.classList.remove('tp-bub--loading'); bubble.removeAttribute('aria-busy'); });
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markSeenBubbles);
  else markSeenBubbles();
  document.addEventListener('shopify:section:load', markSeenBubbles);
}
